<!-- static/src/components/pages/ArticlePage.vue -->
<!-- Individual blog article view at /learn/:slug -->
<template>
  <div>
    <!-- Loading -->
    <div v-if="loading" class="container-wide max-w-4xl py-24 text-center">
      <LoadingSpinner size="lg" label="Loading article…" :show-label="true" />
    </div>

    <!-- 404 -->
    <div v-else-if="!article" class="container-wide max-w-4xl py-24 text-center">
      <h1 class="font-display text-4xl text-ink-900 mb-4">Article not found</h1>
      <p class="text-ink-600 mb-6">The article you're looking for doesn't exist or has been moved.</p>
      <router-link to="/learn" class="btn-primary">Browse all articles</router-link>
    </div>

    <!-- Article -->
    <article v-else>
      <!-- Hero -->
      <section class="relative bg-hero-fade pt-20 pb-12 lg:pt-24">
        <div class="absolute inset-0 bg-grid-ink bg-grid-32 opacity-40 [mask-image:radial-gradient(ellipse_at_top,black_40%,transparent_70%)]"></div>
        <div class="relative container-wide max-w-3xl">
          <!-- Breadcrumb -->
          <nav class="flex items-center gap-2 text-sm text-ink-500 mb-4">
            <router-link to="/learn" class="hover:text-brand-700 transition-colors">Learn</router-link>
            <Icon name="chevron-right" :size="12" />
            <router-link v-if="article.category" :to="`/learn?category=${article.category.slug}`" class="hover:text-brand-700 transition-colors">
              {{ article.category.name }}
            </router-link>
            <Icon v-if="article.category" name="chevron-right" :size="12" />
            <span class="text-ink-500 truncate">{{ article.title }}</span>
          </nav>

          <h1 class="font-display text-4xl lg:text-5xl text-ink-900 leading-[1.1] text-balance">
            {{ article.title }}
          </h1>
          <p v-if="article.excerpt" class="mt-4 text-lg text-ink-600 text-pretty">{{ article.excerpt }}</p>

          <div class="mt-6 flex flex-wrap items-center gap-3 text-sm text-ink-500">
            <span v-if="article.author_name" class="flex items-center gap-1.5">
              <Icon name="user" :size="14" />
              {{ article.author_name }}
            </span>
            <span class="flex items-center gap-1.5">
              <Icon name="clock" :size="14" />
              {{ article.read_time_minutes }} min read
            </span>
            <span v-if="article.published_at" class="flex items-center gap-1.5">
              <Icon name="calendar" :size="14" />
              {{ formatDate(article.published_at) }}
            </span>
            <span v-if="article.category" class="chip chip-brand text-xs">
              {{ article.category.name }}
            </span>
          </div>
        </div>
      </section>

      <!-- Content -->
      <section class="container-wide max-w-3xl py-12">
        <div class="prose prose-lg prose-brand max-w-none article-content" v-html="article.content" />
      </section>

      <!-- Related / CTA -->
      <section class="bg-ink-50 py-16">
        <div class="container-wide max-w-4xl text-center">
          <h2 class="font-display text-3xl text-ink-900 mb-4">Ready to apply what you learned?</h2>
          <p class="text-ink-600 mb-6 max-w-md mx-auto">
            Run a free SEO audit on your site and see how these principles apply to your pages.
          </p>
          <router-link to="/analyze" class="btn-brand btn-lg">
            Run a free audit <Icon name="arrow-right" :size="16" />
          </router-link>
        </div>
      </section>
    </article>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { usePageMeta } from '../../composables/usePageMeta.js';
import { markPrerenderReady } from '../../composables/usePrerenderReady';
import { useContentStore } from '../../stores/contentStore';
import Icon from '../ui/Icon.vue';
import LoadingSpinner from '../ui/LoadingSpinner.vue';

const DEFAULT_TITLE = 'LeadRanks | SEO Intelligence Platform';
const DEFAULT_DESC = 'AI-powered SEO audits, competitor analysis, and actionable recommendations.';

const route = useRoute();
const contentStore = useContentStore();
// Seed from the router-guard prefetch (stores/contentStore.js) so the first
// render is hydration-exact against the SSR snapshot, which is rendered from
// this same store state. Falls back to the normal onMounted fetch otherwise.
const _rec = contentStore.articlesBySlug[route.params.slug];
const _seed = (_rec && _rec.loaded && _rec.fresh) ? _rec.data : null;
const article = ref(_seed);
const loading = ref(!_seed);

usePageMeta(() => {
  const t = article.value?.meta_title || article.value?.title;
  const title = t ? `${t} | LeadRanks Learn` : DEFAULT_TITLE;
  const desc = article.value?.meta_description || article.value?.excerpt || DEFAULT_DESC;
  const image = article.value?.og_image_url || 'https://leadranks.io/leadranks-logo.png';
  const meta = {
    title,
    meta: [
      { name: 'description', content: desc },
      { property: 'og:title', content: title },
      { property: 'og:description', content: desc },
      { property: 'og:image', content: image },
      { property: 'og:type', content: 'article' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
  };

  // Only emit schema once we actually have a real article — no fabricated
  // markup for the loading/404 states, matching Google's "must reflect
  // real, visible page content" requirement.
  if (!article.value) return meta;

  const a = article.value;
  const url = `https://leadranks.io/learn/${a.slug}`;
  meta.jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://leadranks.io/' },
        { '@type': 'ListItem', position: 2, name: 'Learn', item: 'https://leadranks.io/learn' },
        { '@type': 'ListItem', position: 3, name: a.title, item: url },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: a.title,
      description: a.excerpt || desc,
      image,
      url,
      datePublished: a.published_at || a.created_at,
      dateModified: a.updated_at || a.published_at || a.created_at,
      ...(a.author_name ? { author: { '@type': 'Person', name: a.author_name } } : {}),
      publisher: {
        '@type': 'Organization',
        name: 'LeadRanks',
        logo: { '@type': 'ImageObject', url: 'https://leadranks.io/leadranks-logo.png' },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      ...(a.category?.name ? { articleSection: a.category.name } : {}),
    },
  ];
  return meta;
});

const fetchArticle = async (slug) => {
  loading.value = true;
  try {
    const res = await fetch(`/api/blog/articles/${slug}`);
    if (!res.ok) throw new Error('Not found');
    const data = await res.json();
    article.value = data.article;
  } catch {
    article.value = null;
  } finally {
    loading.value = false;
    // Opted out of App.vue's automatic fire (PRERENDER_ASYNC_ROUTES) —
    // fires whether the fetch succeeded or not, since a 404 empty-state is
    // also a legitimate final state worth snapshotting instead of the
    // loading skeleton.
    markPrerenderReady();
  }
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
};

onMounted(() => {
  if (_seed) {
    // Already fetched by the router guard for this mount — don't re-fetch,
    // and consume the freshness marker so a later navigation refreshes.
    contentStore.consumeArticle(route.params.slug);
    markPrerenderReady();
    return;
  }
  fetchArticle(route.params.slug);
});

watch(() => route.params.slug, (slug) => {
  fetchArticle(slug);
});
</script>

<style scoped>
.article-content :deep(h2) {
  @apply font-display text-2xl lg:text-3xl text-ink-900 mt-12 mb-4 leading-tight;
}
.article-content :deep(h3) {
  @apply font-display text-xl text-ink-900 mt-8 mb-3 leading-snug;
}
.article-content :deep(p) {
  @apply text-ink-600 leading-relaxed mb-5;
}
.article-content :deep(strong) {
  @apply text-ink-900 font-semibold;
}
.article-content :deep(a) {
  @apply text-brand-600 underline underline-offset-2 decoration-brand-200 hover:decoration-brand-600 transition-colors;
}
.article-content :deep(code) {
  @apply bg-ink-100 text-ink-800 px-1.5 py-0.5 rounded text-sm font-mono;
}
.article-content :deep(pre) {
  @apply bg-ink-900 text-ink-100 p-5 rounded-xl overflow-x-auto my-6 text-sm;
}
.article-content :deep(pre code) {
  @apply bg-transparent text-inherit px-0 py-0 text-sm;
}
.article-content :deep(blockquote) {
  @apply border-l-4 border-brand-300 pl-5 italic text-ink-600 my-6;
}
.article-content :deep(ul), .article-content :deep(ol) {
  @apply pl-6 mb-5 text-ink-600 leading-relaxed;
}
.article-content :deep(ul) { @apply list-disc; }
.article-content :deep(ol) { @apply list-decimal; }
.article-content :deep(li) { @apply mb-1.5; }
.article-content :deep(img) {
  @apply max-w-full h-auto rounded-xl my-6;
}
.article-content :deep(hr) {
  @apply border-ink-200 my-10;
}
</style>
